import { useCallback, useEffect, useState } from 'react';
import { UnitAllowance, getUnitAllowance } from '../../api/units-client';
import { useAuth } from '../../context/auth-provider';
import { can, PERMISSIONS } from '../../auth/permissions';

// The org's prepaid unit pool, for every screen that can spend one.
//
// There is exactly ONE place this number is computed — org_unit_allowance() in
// the database — and exactly one endpoint that reports it. This hook is how the
// UI reads it. No screen may derive `remaining` from a units list, a building
// count, or a subscription row: those go stale independently and the whole point
// of the pool is that billing and the UI cannot disagree about it.
//
// Permission-aware like use-portfolios: a concierge holds neither unit:read nor
// any billing permission, so the hook settles immediately with `allowed: false`
// and fires no request that would come back 403.
// `units_over` is new on the wire. It is computed SERVER-side (used - bought,
// clamped at 0) and simply carried here — the widening exists so this bundle
// compiles, and keeps working, against a server that does not send it yet.
type AllowanceWithOver = UnitAllowance & { units_over?: number };

export interface UseUnitAllowanceResult {
  allowance: UnitAllowance | null;
  isLoading: boolean;
  error: string | null;
  allowed: boolean;
  // Convenience readers. `remaining` is Infinity when the org is unmetered, so
  // `needed <= remaining` is a safe comparison in every case.
  remaining: number;
  capped: boolean;
  canBuy: boolean;
  // A complimentary account: the out-of-units dialog says so instead of
  // pointing at an owner who cannot buy either.
  comped: boolean;
  // How many units the org is using BEYOND what it bought, straight from the
  // server. `remaining` is clamped at zero in SQL, so without this an
  // over-allocated org reads exactly like an exactly-full one. 0 when the org
  // is within its pool, and 0 from a server that does not report it.
  unitsOver: number;
  // Archived (previously returned) units the org could restore. Pass-through
  // like unitsOver; 0 from a server that does not report it.
  unitsReturned: number;
  reload: () => Promise<void>;
}

export const useUnitAllowance = (): UseUnitAllowanceResult => {
  const { permissions }: { permissions: string[] } = useAuth();
  const allowed = can(permissions, PERMISSIONS.UNIT_READ);

  const [allowance, setAllowance] = useState<UnitAllowance | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(allowed);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!allowed) {
      setAllowance(null);
      setIsLoading(false);

      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      setAllowance(await getUnitAllowance());
    } catch {
      setError('We could not read how many units you have left.');
      setAllowance(null);
    } finally {
      setIsLoading(false);
    }
  }, [allowed]);

  useEffect(() => {
    void load();
  }, [load]);

  // Unmetered and "not loaded yet" both read as Infinity on purpose: a screen
  // must never refuse an action because the allowance has not arrived. The
  // server is the thing that enforces the cap; this is only how the UI explains
  // it in advance.
  const capped = !!allowance?.capped;
  const remaining =
    capped && allowance?.units_remaining != null
      ? allowance.units_remaining
      : Infinity;

  // PASS-THROUGH, exactly like the rest of this hook: the over-cap figure is
  // NOT derived here. Subtracting `units_used` from `units_allowed` on the
  // client would be a second implementation of the pool arithmetic, which is
  // the one thing this hook exists to prevent.
  const unitsOver = (allowance as AllowanceWithOver | null)?.units_over ?? 0;

  return {
    allowance,
    isLoading,
    error,
    allowed,
    remaining,
    capped,
    canBuy: !!allowance?.can_buy,
    comped: !!allowance?.comped,
    unitsOver,
    unitsReturned: allowance?.units_returned ?? 0,
    reload: load,
  };
};
